import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, FileText, ShoppingCart, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { useCompany, fmtMoney } from "@/lib/company";

export default function Invoices({ direction: initialDirection = "sale" }) {
  const { active } = useCompany();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [direction, setDirection] = useState(initialDirection);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!active) return;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ direction });
        if (status) params.set("status", status);
        const { data } = await api.get(`/invoices?${params.toString()}`);
        setRows(data.invoices || []);
      } finally { setLoading(false); }
    })();
  }, [active, direction, status]);

  const filtered = rows.filter((r) =>
    !q || (r.number || "").toLowerCase().includes(q.toLowerCase()) ||
    (r.party_snapshot?.name || "").toLowerCase().includes(q.toLowerCase())
  );

  const isPurchase = direction === "purchase";

  return (
    <div className="space-y-4" data-testid="invoices-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="overline mb-1">{isPurchase ? "Purchases" : "Invoices"}</div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#0F172A]">
            {isPurchase ? "Purchase Bills" : "Sales Invoices"}
          </h1>
        </div>
        <Link
          to={isPurchase ? "/app/purchases/new" : "/app/invoices/new"}
          data-testid={isPurchase ? "purchases-new-btn" : "invoices-new-btn"}
          className="btn-primary w-full sm:w-auto justify-center"
        >
          <Plus size={14}/> {isPurchase ? "New Bill" : "New Invoice"}
        </Link>
      </div>

      <div className="flex gap-1 border-b border-[#E2E8F0] overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0">
        <button data-testid="tab-sale" onClick={() => setDirection("sale")}
          className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap shrink-0 ${direction === "sale" ? "border-[#1E3A8A] text-[#1E3A8A]" : "border-transparent text-[#64748B]"}`}>
          <TrendingUp size={14}/> Sales
        </button>
        <button data-testid="tab-purchase" onClick={() => setDirection("purchase")}
          className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap shrink-0 ${direction === "purchase" ? "border-[#1E3A8A] text-[#1E3A8A]" : "border-transparent text-[#64748B]"}`}>
          <ShoppingCart size={14}/> Purchases
        </button>
      </div>

      <div className="card-surface p-4">
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[220px]">
            <Search size={16} className="text-[#94A3B8]"/>
            <input data-testid="invoices-search" className="input-clean" placeholder="Search number or party…" value={q} onChange={(e) => setQ(e.target.value)}/>
          </div>
          <select data-testid="invoices-status-filter" className="input-clean" value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 160 }}>
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="partial">Partial</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
        {filtered.length === 0 ? (
          <div className="text-center py-12">
            <FileText size={48} className="mx-auto text-[#CBD5E1] mb-3"/>
            <p className="text-[#64748B] text-sm">
              {loading ? "Loading…" : q ? "No matching records." : (isPurchase ? "No purchase bills yet." : "No invoices yet.")}
            </p>
          </div>
        ) : (
          <table className="table-clean">
            <thead>
              <tr>
                <th>Number</th><th>Type</th><th>Party</th><th>Date</th>
                <th className="text-right">Amount</th>
                <th className="text-right">Paid</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inv) => (
                <tr key={inv.id} data-testid={`invoice-row-${inv.id}`}
                  className="cursor-pointer"
                  onClick={() => navigate(`/app/invoices/${inv.id}`)}>
                  <td className="font-mono-data font-medium text-[#1E3A8A]">{inv.number}</td>
                  <td>
                    <span className="chip chip-neutral text-[10px]">
                      {inv.invoice_type === "tax_invoice" ? "Tax" : "Bill of Supply"}
                    </span>
                  </td>
                  <td>{inv.party_snapshot?.name}</td>
                  <td className="font-mono-data text-[#64748B]">{inv.invoice_date}</td>
                  <td className="text-right font-mono-data font-semibold">{fmtMoney(inv.grand_total, inv.currency)}</td>
                  <td className="text-right font-mono-data text-[#16A34A]">{fmtMoney(inv.paid_amount, inv.currency)}</td>
                  <td>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className={`chip chip-${inv.status === "paid" ? "success" : inv.status === "draft" ? "neutral" : inv.status === "cancelled" ? "danger" : "warn"}`}>
                        {inv.status}
                      </span>
                      {inv.stripe_session_id && !["paid","draft","cancelled"].includes(inv.status) && (
                        <span
                          data-testid={`stripe-badge-${inv.id}`}
                          className="chip chip-warn text-[10px] whitespace-nowrap"
                          title="Stripe pay-link created — awaiting customer payment"
                        >
                          <svg viewBox="0 0 24 24" width="10" height="10" fill="currentColor" style={{marginRight:2}}><path d="M12 2a10 10 0 100 20 10 10 0 000-20zm0 5a1 1 0 011 1v4a1 1 0 01-2 0V8a1 1 0 011-1zm0 8a1 1 0 110 2 1 1 0 010-2z"/></svg>
                          Pay Pending
                        </span>
                      )}
                      {inv.stripe_session_id && inv.status === "paid" && (
                        <span
                          data-testid={`stripe-badge-${inv.id}`}
                          className="chip chip-success text-[10px] whitespace-nowrap"
                          title="Paid via Stripe"
                        >
                          Paid · Stripe
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
